import { useCallback, useEffect, useMemo, useState } from 'react'
import * as echarts from 'echarts'
import type { Time } from 'lightweight-charts'
import type { ScreenerOIDataPoint, ScreenerTickerData } from '@/types'
import { MergedOIChart } from './MergedOIChart'
import { OIPanel } from './OIPanel'
import { PriceLineChart } from './PriceLineChart'
import { exchangeColor, SCREENER_CHART_GROUP } from './chartUtils'
import { useExchangeChartOrder } from './useExchangeChartOrder'
import { useExchangeChartReordering } from './useExchangeChartReordering'
import { useChartCollapsePreference } from './useChartCollapsePreference'
import { ResizableChart } from './ResizableChart'
import { CVDChart } from './CVDChart'
import { FEATURE_SCREENER_TRADES_ENABLED } from '@/lib/featureFlags'
import type { ChartAlertControls } from './ChartLevelAlerts'
import type { RulerExchangeSeries } from './ChartRuler'
import type { ChartLayoutPreferenceControls } from '@/hooks/useChartLayoutPreferences'

export type ViewMode = 'merged' | 'separate'

interface ChartLayoutManagerProps {
  tickerData: ScreenerTickerData | null
  symbol: string
  mode: ViewMode
  selectedExchanges: string[]
  priceExchange: string
  interval: string
  orderStorageKey: string
  alertControls?: ChartAlertControls
  layoutPreferences?: ChartLayoutPreferenceControls
  oiLineMode?: boolean
}
export function ChartLayoutManager({
  tickerData,
  symbol,
  mode,
  selectedExchanges,
  priceExchange,
  interval,
  orderStorageKey,
  alertControls,
  layoutPreferences,
  oiLineMode = true,
}: ChartLayoutManagerProps) {
  const [syncedTime, setSyncedTime] = useState<Time | null>(null)
  const { isCollapsed, toggleChart } = useChartCollapsePreference(orderStorageKey, layoutPreferences && {
    collapsed: layoutPreferences.value.collapsed, onChange: layoutPreferences.setCollapsed,
  })
  const paneHeight = (id: string, defaultHeight: number) => layoutPreferences ? {
    height: layoutPreferences.value.heights[id] ?? defaultHeight,
    onHeightChange: (height: number) => layoutPreferences.setHeight(id, height),
  } : {}
  const handleCrosshairMove = useCallback((time: Time | null) => setSyncedTime(time), [])

  useEffect(() => {
    echarts.connect(SCREENER_CHART_GROUP)
    return () => echarts.disconnect(SCREENER_CHART_GROUP)
  }, [])

  const oiExchanges = useMemo(() => {
    if (!tickerData?.exchanges) return []
    return Object.entries(tickerData.exchanges)
      .filter(([, data]) => tickerData.history_window != null || (data.oi_history?.length || 0) > 0)
      .map(([exchange]) => exchange)
      .sort()
  }, [tickerData])

  const visibleExchanges = useMemo(() => {
    const selected = new Set(selectedExchanges)
    return oiExchanges.filter((exchange) => selected.has(exchange))
  }, [oiExchanges, selectedExchanges])

  const cvdExchanges = useMemo(() => Object.entries(tickerData?.exchanges || {})
    .filter(([, data]) => data.cvd_history !== undefined || data.cvd_error != null)
    .map(([exchange]) => exchange).sort(), [tickerData])
  const chartExchanges = useMemo(() => [...new Set([...oiExchanges, ...cvdExchanges])], [oiExchanges, cvdExchanges])
  const { orderedExchanges, moveExchange } = useExchangeChartOrder(chartExchanges, orderStorageKey, layoutPreferences && {
    order: layoutPreferences.value.order, onChange: layoutPreferences.setOrder,
  })
  const orderedVisibleExchanges = orderedExchanges.filter((exchange) => visibleExchanges.includes(exchange))
  const orderedCvdExchanges = orderedExchanges.filter((exchange) => cvdExchanges.includes(exchange) && selectedExchanges.includes(exchange))
  const reorderContext = JSON.stringify([symbol, mode, orderStorageKey])
  const oiReordering = useExchangeChartReordering(orderedVisibleExchanges, moveExchange, reorderContext)
  const cvdReordering = useExchangeChartReordering(orderedCvdExchanges, moveExchange, reorderContext, ' CVD')
  const lastExpandedExchange = orderedVisibleExchanges.filter((exchange) => !isCollapsed(`oi:${exchange}`)).slice(-1)[0]

  const effectivePriceExchange = useMemo(() => {
    if (priceExchange && tickerData?.exchanges?.[priceExchange]) return priceExchange
    return visibleExchanges[0] || chartExchanges[0] || ''
  }, [priceExchange, tickerData, visibleExchanges, chartExchanges])

  const seriesByExchange = useMemo(() => {
    const result: Record<string, ScreenerOIDataPoint[]> = {}
    for (const exchange of visibleExchanges) {
      result[exchange] = tickerData?.exchanges?.[exchange]?.oi_history || []
    }
    return result
  }, [tickerData, visibleExchanges])

  const rulerExchanges = useMemo<RulerExchangeSeries[]>(() => [...new Set(selectedExchanges)].map((exchange, index) => {
    const data = tickerData?.exchanges?.[exchange]
    const visibleIndex = visibleExchanges.indexOf(exchange)
    return {
      id: exchange,
      label: exchange,
      color: exchangeColor(visibleIndex >= 0 ? visibleIndex : index),
      oiData: data?.oi_history || [],
      candles: data?.candles || [],
      currentPrice: data?.last_price,
    }
  }), [selectedExchanges, tickerData, visibleExchanges])

  const fillThrough = useMemo(() => {
    if (tickerData?.oi_history_until != null) return tickerData.oi_history_until
    // Older servers have no query watermark. Align exchanges only to received
    // chart history, never to the browser clock or unrelated ticker prices.
    const timestamps = Object.values(tickerData?.exchanges || {}).flatMap((data) =>
      (data.oi_history || []).map((point) => point.timestamp).filter(Number.isFinite))
    return timestamps.length > 0 ? timestamps.reduce((latest, value) => Math.max(latest, value), timestamps[0]) : null
  }, [tickerData])

  if (!tickerData) {
    return <div className="flex h-96 items-center justify-center text-sm text-slate-500">Select a ticker to view charts</div>
  }

  if (chartExchanges.length === 0) {
    return (
      <div className="flex h-96 items-center justify-center rounded-xl border border-dashed border-slate-800 bg-slate-950/30 text-sm text-slate-500">
        No stored chart history is available for {symbol}.
      </div>
    )
  }

  const priceData = effectivePriceExchange ? tickerData.exchanges[effectivePriceExchange] : null

  return (
    <div className="flex w-full flex-col gap-2.5">
      <div className="px-1 text-[10px] text-slate-500">Double-click a price or OI graph to set a personal alert · Manage levels and delivery in My alerts · Drag right-axis values to scale, click to fit · Drag the bottom handle to resize height, double-click to reset · Ruler: Shift-click or Shift-drag · Click to finish · Esc to clear · In merged view, select near an exchange line</div>
      <ResizableChart
        {...paneHeight('price', 330)}
        storageKey={`${orderStorageKey}:height:price`}
        label="price chart" defaultHeight={330} minHeight={180} collapsed={isCollapsed('price')}
      >
        {(height) => (
          <PriceLineChart
            height={height}
            collapsed={isCollapsed('price')}
            onToggleCollapse={() => toggleChart('price')}
            rulerExchanges={rulerExchanges}
            alertControls={alertControls}
            key={`${symbol}:${effectivePriceExchange}:${interval}`}
            oiData={priceData?.oi_history || []}
            candles={priceData?.candles || []}
            currentPrice={priceData?.last_price}
            market={priceData?.market === 'spot' ? 'spot' : 'futures'}
            candleInterval={tickerData.price_interval}
            symbol={symbol}
            exchange={effectivePriceExchange}
            interval={interval}
            chartGroup={SCREENER_CHART_GROUP}
            onCrosshairMove={handleCrosshairMove}
            syncedTime={syncedTime}
          />
        )}
      </ResizableChart>

      {visibleExchanges.length === 0 ? (
        <div className="flex h-40 items-center justify-center rounded-xl border border-dashed border-slate-800 text-sm text-slate-500">
          Select at least one OI exchange.
        </div>
      ) : mode === 'merged' ? (
        <ResizableChart
          {...paneHeight('merged-oi', 440)}
          storageKey={`${orderStorageKey}:height:merged-oi`}
          label="merged OI chart" defaultHeight={440} minHeight={240} collapsed={isCollapsed('merged-oi')}
        >
          {(height) => (
            <MergedOIChart
              lineMode={oiLineMode}
              height={height}
              collapsed={isCollapsed('merged-oi')}
              onToggleCollapse={() => toggleChart('merged-oi')}
              alertControls={alertControls}
              symbol={symbol}
              key={`${symbol}:${interval}:${visibleExchanges.join(',')}`}
              seriesByExchange={seriesByExchange}
              exchangeData={tickerData.exchanges}
              candleInterval={tickerData.price_interval}
              interval={interval}
              fillThrough={fillThrough}
              chartGroup={SCREENER_CHART_GROUP}
              onCrosshairMove={handleCrosshairMove}
              syncedTime={syncedTime}
            />
          )}
        </ResizableChart>
      ) : (
        <div className="flex flex-col gap-1.5">
          {orderedVisibleExchanges.map((exchange) => {
            const data = tickerData.exchanges[exchange]
            return (
              <div
                key={`${symbol}:${exchange}:${interval}`}
                data-exchange-chart={exchange}
                {...oiReordering.getDropProps(exchange)}
              >
                <ResizableChart
                  {...paneHeight(`oi:${exchange}`, 145)}
                  storageKey={`${orderStorageKey}:height:oi:${exchange}`}
                  label={`${exchange} OI chart`} defaultHeight={145} minHeight={120} collapsed={isCollapsed(`oi:${exchange}`)}
                >
                  {(height) => (
                    <OIPanel
                      lineMode={oiLineMode}
                      height={height}
                      collapsed={isCollapsed(`oi:${exchange}`)}
                      onToggleCollapse={() => toggleChart(`oi:${exchange}`)}
                      alertControls={alertControls}
                      symbol={symbol}
                      oiData={data.oi_history || []}
                      candles={data.candles || []}
                      currentPrice={data.last_price}
                      candleInterval={tickerData.price_interval}
                      currentOI={data.oi_current}
                      fundingData={data}
                      volumeHistory={data.volume_history}
                      volumeInterval={data.volume_interval}
                      exchange={exchange}
                      interval={interval}
                      fillThrough={fillThrough}
                      color={exchangeColor(visibleExchanges.indexOf(exchange))}
                      chartGroup={SCREENER_CHART_GROUP}
                      onCrosshairMove={handleCrosshairMove}
                      syncedTime={syncedTime}
                      showXAxis={exchange === lastExpandedExchange}
                      orderControls={oiReordering.renderControls(exchange)}
                    />
                  )}
                </ResizableChart>
              </div>
            )
          })}
        </div>
      )}
      {FEATURE_SCREENER_TRADES_ENABLED && orderedCvdExchanges.map((exchange) => {
        const data = tickerData.exchanges[exchange]
        return (
          <div key={`cvd:${exchange}`} data-cvd-chart={exchange} {...cvdReordering.getDropProps(exchange)}>
            <ResizableChart
              {...paneHeight(`cvd:${exchange}`, 200)}
              storageKey={`${orderStorageKey}:height:cvd:${exchange}`}
              label={`${exchange} CVD chart`} defaultHeight={200} minHeight={140}
              collapsed={isCollapsed(`cvd:${exchange}`)}>
              {(height) => (
                <CVDChart
                  key={`${symbol}:${exchange}:${data.cvd_interval || interval}`}
                  symbol={symbol} exchange={exchange}
                  candles={data.cvd_history || []}
                  interval={data.cvd_interval || interval}
                  unit={data.cvd_unit || 'coins'}
                  baselineTimestamp={data.cvd_baseline_ts}
                  hasGaps={data.cvd_has_gaps}
                  error={data.cvd_error}
                  height={height}
                  collapsed={isCollapsed(`cvd:${exchange}`)}
                  onToggleCollapse={() => toggleChart(`cvd:${exchange}`)}
                  orderControls={cvdReordering.renderControls(exchange)}
                  chartGroup={SCREENER_CHART_GROUP}
                  onCrosshairMove={handleCrosshairMove}
                  syncedTime={syncedTime}
                />
              )}
            </ResizableChart>
          </div>
        )
      })}
    </div>
  )
}
